<template>
  <div class="swin" :class="{ 'swin--compact': compact }">
    <div class="swin__toggle">
      <label class="swin__switch">
        <input type="checkbox" :checked="enabled" @change="setEnabled($event.target.checked)">
        <span class="track"></span>
      </label>
      <div class="swin__toggle-body">
        <div class="swin__toggle-title">{{ label }}</div>
        <div v-if="!enabled" class="swin__inherit">
          <template v-if="inheritedSummary">Using {{ inheritedLabel }}: <strong>{{ inheritedSummary }}</strong></template>
          <template v-else>Using {{ inheritedLabel }} — <span class="text-muted">no window set, sends any time</span></template>
        </div>
      </div>
    </div>

    <div v-if="enabled" class="swin__body">
      <!-- Day chips -->
      <div class="swin__days">
        <button
          v-for="d in weekdays"
          :key="d.value"
          type="button"
          class="swin__day"
          :class="{ 'is-on': days.includes(d.value) }"
          @click="toggleDay(d.value)"
        >{{ d.label }}</button>
      </div>

      <!-- Time ranges -->
      <div class="swin__ranges">
        <div v-for="(r, i) in ranges" :key="i" class="swin__range">
          <input type="time" step="900" class="form-control form-control-sm swin__time" :value="minToTime(r.start)" @input="setRange(i, 'start', $event.target.value)">
          <span class="swin__dash">to</span>
          <input type="time" step="900" class="form-control form-control-sm swin__time" :value="minToTime(r.end)" @input="setRange(i, 'end', $event.target.value)">
          <button type="button" class="btn btn-outline-danger btn-sm swin__rm" :disabled="ranges.length <= 1" title="Remove range" @click="removeRange(i)">
            <CygnusIcon name="trash" />
          </button>
        </div>
        <button type="button" class="btn btn-outline-secondary btn-sm swin__add" @click="addRange">
          <CygnusIcon name="plus" class="me-1" />Add time range
        </button>
      </div>

      <!-- Timezone -->
      <div class="swin__tz">
        <label class="form-label small text-muted mb-0">Timezone</label>
        <input type="text" class="form-control form-control-sm" :value="timezone" placeholder="Europe/London" @input="setTimezone($event.target.value)">
      </div>

      <!-- Live summary + client warnings -->
      <p v-if="summary" class="swin__summary"><CygnusIcon name="clock" class="me-1" />{{ summary }}</p>
      <p v-if="warning" class="swin__warn"><CygnusIcon name="warning" class="me-1" />{{ warning }}</p>
    </div>
  </div>
</template>

<script>
import { minutesToHHMM, hhmmToMinutes, summariseWindow } from "./util/sendWindowSummary";

// Default single range: 09:00–17:00 every weekday, minute-precise.
const DEFAULT_RANGE = () => ({ start: 540, end: 1020 });

export default {
  name: 'SendWindowEditor',
  props: {
    // The override spec { timezone, days, ranges } or null (= inherit).
    modelValue: { type: Object, default: null },
    // Copy shown on the toggle.
    label: { type: String, default: 'Override send window' },
    // What "off" inherits — e.g. "workspace window" / "flow window".
    inheritedLabel: { type: String, default: 'workspace window' },
    // Human summary of the inherited window, shown when the override is off.
    inheritedSummary: { type: String, default: '' },
    // Default timezone when the override is first enabled.
    defaultTimezone: { type: String, default: 'Europe/London' },
    compact: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      weekdays: [
        { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
        { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 7, label: 'Sun' },
      ],
      enabled: false,
      days: [1, 2, 3, 4, 5],
      ranges: [DEFAULT_RANGE()],
      timezone: '',
    };
  },
  computed: {
    summary() {
      if (!this.enabled) return '';
      return summariseWindow({ days: this.days, ranges: this.ranges, timezone: this.timezone });
    },
    // Client-side sanity note (the server is the real validator → 422 on save).
    warning() {
      if (!this.enabled) return '';
      if (!this.days.length) return 'Pick at least one day.';
      const sorted = this.ranges.slice().sort((a, b) => a.start - b.start);
      for (const r of sorted) {
        if (r.end <= r.start) return 'Each range must end after it starts.';
        if (r.end - r.start < 15) return 'Each range must be at least 15 minutes long.';
      }
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].start < sorted[i - 1].end) return 'Time ranges must not overlap.';
      }
      return '';
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(v) { this.hydrate(v); },
    },
  },
  methods: {
    minutesToHHMM,
    minToTime(m) { return minutesToHHMM(m); },
    hydrate(v) {
      if (v && Array.isArray(v.days) && Array.isArray(v.ranges) && v.days.length && v.ranges.length) {
        this.enabled = true;
        this.days = [...v.days].map(Number).filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b);
        this.ranges = v.ranges.map((r) => ({ start: Number(r.start) || 0, end: Number(r.end) || 0 }));
        this.timezone = v.timezone || this.defaultTimezone;
      } else {
        this.enabled = false;
        // Keep sensible defaults ready for when the user toggles it on.
        if (!this.timezone) this.timezone = this.defaultTimezone;
      }
    },
    emit() {
      if (!this.enabled) { this.$emit('update:modelValue', null); return; }
      this.$emit('update:modelValue', {
        timezone: (this.timezone || this.defaultTimezone || 'Europe/London').trim(),
        days: [...this.days].sort((a, b) => a - b),
        ranges: this.ranges.map((r) => ({ start: r.start, end: r.end })),
      });
    },
    setEnabled(on) {
      this.enabled = on;
      if (on) {
        if (!this.days.length) this.days = [1, 2, 3, 4, 5];
        if (!this.ranges.length) this.ranges = [DEFAULT_RANGE()];
        if (!this.timezone) this.timezone = this.defaultTimezone;
      }
      this.emit();
    },
    toggleDay(v) {
      const i = this.days.indexOf(v);
      if (i === -1) this.days.push(v); else this.days.splice(i, 1);
      this.days.sort((a, b) => a - b);
      this.emit();
    },
    setRange(i, key, hhmm) {
      if (!this.ranges[i]) return;
      this.ranges[i][key] = hhmmToMinutes(hhmm);
      this.emit();
    },
    addRange() {
      // Seed a new range after the last one (18:00–19:00 by default).
      const last = this.ranges[this.ranges.length - 1];
      const start = last ? Math.min(1380, last.end + 60) : 1080;
      this.ranges.push({ start, end: Math.min(1440, start + 60) });
      this.emit();
    },
    removeRange(i) {
      if (this.ranges.length <= 1) return;
      this.ranges.splice(i, 1);
      this.emit();
    },
    setTimezone(v) { this.timezone = v; this.emit(); },
  },
};
</script>

<style>
.swin { border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px); padding: 12px 14px; background: var(--m-bg-subtle, #F8FAFC); }
.swin--compact { padding: 10px 12px; }
.swin__toggle { display: flex; align-items: flex-start; gap: 12px; }
.swin__switch { position: relative; width: 40px; height: 22px; flex: none; margin-top: 1px; }
.swin__switch input { opacity: 0; width: 0; height: 0; }
.swin__switch .track { position: absolute; inset: 0; background: #CBD2DE; border-radius: 999px; transition: 0.15s; cursor: pointer; }
.swin__switch .track::before { content: ""; position: absolute; left: 2px; top: 2px; width: 18px; height: 18px; background: #fff; border-radius: 50%; transition: 0.15s; box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
.swin__switch input:checked + .track { background: var(--m-accent, #4F46E5); }
.swin__switch input:checked + .track::before { transform: translateX(18px); }
.swin__toggle-title { font-weight: 600; font-size: 0.86rem; }
.swin__inherit { font-size: 0.78rem; color: var(--m-text-secondary, #697086); margin-top: 2px; }
.swin__body { margin-top: 12px; display: flex; flex-direction: column; gap: 12px; }
.swin__days { display: flex; flex-wrap: wrap; gap: 6px; }
.swin__day {
  border: 1px solid var(--m-border, #E2E8F0); background: var(--m-bg-card, #FFF);
  color: var(--m-text-secondary, #475569); border-radius: 100px; padding: 3px 12px;
  font-size: 0.78rem; line-height: 1.4; cursor: pointer; font-family: inherit;
}
.swin__day.is-on { background: var(--m-accent, #4F46E5); border-color: var(--m-accent, #4F46E5); color: #FFF; }
.swin__ranges { display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
.swin__range { display: flex; align-items: center; gap: 8px; }
.swin__time { max-width: 120px; }
.swin__dash { font-size: 0.8rem; color: var(--m-text-secondary, #475569); }
.swin__tz { display: flex; align-items: center; gap: 8px; }
.swin__tz input { max-width: 220px; }
.swin__summary { font-size: 0.8rem; color: var(--m-text-secondary, #475569); margin: 0; }
.swin__warn { font-size: 0.78rem; color: var(--m-status-warning, #B45309); margin: 0; }
</style>
